import { FC } from 'react'
import { documentoDaPrescricao } from '../../core/montagem/historico'
import { DocumentoEmAssinatura, EstadoDocumento } from '../../hooks/useAssinaturaDocumentos'
import { IconPaths } from '_metronic/helpers'
import styles from '../../Prescricao.module.scss'
import { Marca, MarcaEstado } from '../comum/MarcaEstado'
import { useRotulos } from '../comum/useRotulos'
import { Button, InfoAlert } from 'app/components'

const MARCA: Record<EstadoDocumento, { marca: Marca; classe: string }> = {
  fila: { marca: IconPaths.Relogio, classe: styles.estadoNeutro },
  assinando: { marca: 'andamento', classe: styles.estadoAndamento },
  assinado: { marca: IconPaths.Check, classe: styles.estadoOk },
  erro: { marca: IconPaths.Alert, classe: styles.estadoErro },
}

type IProps = {
  documentos: DocumentoEmAssinatura[]
  preparando: boolean
  erroPreparo?: string
  /** Rascunhos do rollback que o backend não apagou. */
  rascunhosNaoApagados: number
  onTentarDeNovo: (ids?: number[]) => void
  onRepetirPreparo: () => void
  onContinuarSemErros: () => void
  onFechar: () => void
}

/** Progresso por documento: na fila, assinando, assinado ou erro (só esse é tentado de novo). */
export const EtapaProgresso: FC<IProps> = ({
  documentos,
  preparando,
  erroPreparo,
  rascunhosNaoApagados,
  onTentarDeNovo,
  onRepetirPreparo,
  onContinuarSemErros,
  onFechar,
}) => {
  const { t, tituloDocumento } = useRotulos()
  const total = documentos.length
  const assinados = documentos.filter((doc) => doc.estado === 'assinado').length
  const comErro = documentos.filter((doc) => doc.estado === 'erro')
  const andando =
    preparando || documentos.some((doc) => doc.estado === 'assinando' || doc.estado === 'fila')
  const parcial = !andando && comErro.length > 0
  const tudoOk = total > 0 && assinados === total

  if (erroPreparo) {
    return (
      <div className='d-flex flex-column gap-4'>
        <p className={`m-0 ${styles.mensagemErro}`} role='alert'>
          {t('prescricao.assinatura.erro_preparo', { erro: erroPreparo })}
        </p>
        {rascunhosNaoApagados > 0 && (
          <InfoAlert variant='warning' size='sm' className='mb-0'>
            {t('prescricao.rascunhos_orfaos.texto', { quantidade: rascunhosNaoApagados })}
          </InfoAlert>
        )}
        <div className='d-flex justify-content-end gap-2'>
          <Button className='btn-secondary' onClick={onFechar}>
            {t('prescricao.assinatura.voltar_montagem')}
          </Button>
          <Button className='btn-primary' onClick={onRepetirPreparo}>
            {t('prescricao.assinatura.tentar_de_novo')}
          </Button>
        </div>
      </div>
    )
  }

  const titulo = preparando
    ? t('prescricao.assinatura.preparando')
    : tudoOk
    ? t('prescricao.assinatura.todos_assinados', { quantidade: total })
    : parcial
    ? t('prescricao.assinatura.parcial', { assinados, erros: comErro.length })
    : t('prescricao.assinatura.assinando')

  return (
    <div className='d-flex flex-column gap-4'>
      {/* Os anúncios ficam no AssinaturaModal, numa região que já existia antes desta etapa. */}
      <div className='d-flex flex-column gap-2'>
        <div className='d-flex justify-content-between align-items-baseline gap-3'>
          <span
            className={`fs-5 fw-bold ${
              tudoOk ? styles.estadoOk : parcial ? styles.estadoErro : ''
            }`}
          >
            {titulo}
          </span>
          {total > 0 && (
            <span className={`fs-7 ${styles.textoSecundario}`}>
              {t('prescricao.assinatura.contagem', { assinados, total })}
            </span>
          )}
        </div>
        <div
          role='progressbar'
          aria-label={t('prescricao.assinatura.progresso')}
          aria-valuemin={0}
          aria-valuemax={total || 1}
          aria-valuenow={assinados}
          className={`${styles.progresso} ${
            parcial ? styles.progressoErro : tudoOk ? styles.progressoOk : ''
          }`}
        >
          <div style={{ width: `${total ? Math.round((assinados / total) * 100) : 0}%` }} />
        </div>
      </div>

      {total > 0 && (
        <ul className={`list-unstyled m-0 border rounded ${styles.listaRolavel}`}>
          {documentos.map((doc, indice) => {
            const titulo = tituloDocumento(documentoDaPrescricao(doc.prescricao))
            const { marca, classe } = MARCA[doc.estado]
            return (
              <li
                key={doc.prescricao.id}
                className={`d-flex align-items-center gap-3 px-4 py-3 ${
                  indice ? 'border-top' : ''
                } ${doc.estado === 'erro' ? styles.linhaComErro : ''}`}
              >
                <div className={`flex-grow-1 d-flex flex-column ${styles.encolhe}`}>
                  <span className='fw-semibold'>{titulo}</span>
                  {doc.estado === 'erro' && (
                    <span className={`fs-7 ${styles.estadoErro}`}>
                      {doc.semSaldoSncr ? t('prescricao.assinatura.erro_saldo') : doc.erro}
                    </span>
                  )}
                </div>
                <span className={`fw-bold text-nowrap ${styles.comIcone} ${classe}`}>
                  <MarcaEstado marca={marca} />
                  {t(`prescricao.assinatura.estado.${doc.estado}`)}
                </span>
                {doc.estado === 'erro' && (
                  <Button
                    size='btn-sm'
                    className='btn-primary'
                    onClick={() => onTentarDeNovo([doc.prescricao.id])}
                    ariaLabel={t('prescricao.assinatura.tentar_um_aria', { titulo })}
                  >
                    {t('prescricao.assinatura.tentar_de_novo')}
                  </Button>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {parcial && (
        <div className='d-flex flex-wrap justify-content-end gap-2'>
          <Button className='btn-secondary' onClick={onContinuarSemErros}>
            {t('prescricao.assinatura.continuar_sem', { quantidade: comErro.length })}
          </Button>
          <Button className='btn-primary' onClick={() => onTentarDeNovo()}>
            {t('prescricao.assinatura.tentar_erros')}
          </Button>
        </div>
      )}
    </div>
  )
}
